import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { fetchCategoriesForMenu } from '@/api/categories';
import { fetchProductsByMenuAndCategory } from '@/api/products';
import { fetchPublicMenus, fetchRestaurantsForMenu, fetchCombosForMenu, } from '@/api/menus';
import { getStoredToken } from '@/api/auth';
import { fetchMyProfile } from '@/api/me';
import { useAsyncData } from '@/hooks/useAsyncData';
export function useCatalogPageData(checkoutMenuId, setCheckoutMenuId, setPickupRestaurantId) {
    const location = useLocation();
    const navigate = useNavigate();
    const { menuId: menuIdParam, categoryId: categoryIdParam } = useParams();
    const menuId = menuIdParam ? Number(menuIdParam) : NaN;
    const validMenuId = Number.isFinite(menuId);
    const [searchParams] = useSearchParams();
    const categoryFromPath = categoryIdParam ? Number(categoryIdParam) : NaN;
    const categoryFromQuery = Number(searchParams.get('category'));
    const combosMode = searchParams.get('combos') === '1';
    const isLegacyProductsPath = location.pathname.endsWith('/products');
    const [selectedCategoryId, setSelectedCategoryId] = useState(null);
    const { data: categories, loading: categoriesLoading, error: categoriesError } = useAsyncData(validMenuId ? `categories-${menuId}` : null, () => fetchCategoriesForMenu(menuId), { enabled: validMenuId });
    const { data: menuCombos, loading: combosLoading } = useAsyncData(validMenuId ? `combos-${menuId}` : null, () => fetchCombosForMenu(menuId), { enabled: validMenuId });
    const { data: menus } = useAsyncData('public-menus', () => fetchPublicMenus());
    const { data: restaurants, loading: restaurantsLoading } = useAsyncData(checkoutMenuId != null ? `restaurants-${checkoutMenuId}` : null, async (signal) => {
        const list = await fetchRestaurantsForMenu(checkoutMenuId);
        if (!signal.aborted) {
            setPickupRestaurantId((prev) => {
                if (prev != null && list.some((r) => r.id === prev))
                    return prev;
                if (list.length === 1)
                    return list[0].id;
                return null;
            });
        }
        return list;
    }, { enabled: checkoutMenuId != null });
    const { data: products, loading: productsLoading, error: productsError } = useAsyncData(validMenuId && !combosMode && selectedCategoryId != null
        ? `products-${menuId}-${selectedCategoryId}`
        : null, () => fetchProductsByMenuAndCategory(menuId, selectedCategoryId), { enabled: validMenuId && !combosMode && selectedCategoryId != null });
    const { data: bonusBalance } = useAsyncData(getStoredToken() ? 'profile-bonus' : null, async () => {
        const p = await fetchMyProfile();
        return p.bonusBalance;
    }, { enabled: !!getStoredToken() });
    useEffect(() => {
        if (validMenuId)
            setCheckoutMenuId(menuId);
    }, [menuId, validMenuId, setCheckoutMenuId]);
    useEffect(() => {
        const cats = categories ?? [];
        if (cats.length === 0) {
            setSelectedCategoryId(null);
            return;
        }
        if (combosMode) {
            setSelectedCategoryId(null);
            return;
        }
        const fromUrl = Number.isFinite(categoryFromPath)
            ? categoryFromPath
            : isLegacyProductsPath && Number.isFinite(categoryFromQuery)
                ? categoryFromQuery
                : NaN;
        const valid = cats.some((c) => c.id === fromUrl);
        if (valid) {
            setSelectedCategoryId(fromUrl);
            if (isLegacyProductsPath) {
                navigate(`/menu/${menuId}/category/${fromUrl}`, { replace: true });
            }
            return;
        }
        const fallbackCategoryId = cats[0].id;
        setSelectedCategoryId(fallbackCategoryId);
        navigate(`/menu/${menuId}/category/${fallbackCategoryId}`, { replace: true });
    }, [
        categories,
        categoryFromPath,
        categoryFromQuery,
        combosMode,
        isLegacyProductsPath,
        menuId,
        navigate,
    ]);
    return {
        menuId,
        validMenuId,
        combosMode,
        categories: categories ?? [],
        categoriesLoading,
        categoriesError: categoriesError ? 'Не удалось загрузить категории' : null,
        products: products ?? [],
        productsLoading,
        productsError: productsError ? 'Не удалось загрузить товары' : null,
        menuCombos: menuCombos ?? [],
        combosLoading,
        menus: menus ?? [],
        restaurants: restaurants ?? [],
        restaurantsLoading,
        selectedCategoryId,
        bonusBalance: bonusBalance ?? null,
        navigate,
    };
}
